import { createSlice } from "@reduxjs/toolkit";
import {
  clearStoredAuthSession,
  clearUserScopedCache,
  getStoredAuthSession,
  hasAuthTokens,
  normalizeAuthSession,
  persistAuthSession,
} from "./authSession";

export const loadAuthState = () => {
  try {
    const storedSession = getStoredAuthSession();

    if (storedSession && hasAuthTokens(storedSession)) {
      return {
        user: storedSession,
        isAuthenticated: true,
        loading: false,
        error: null,
      };
    }

    return {
      user: null,
      isAuthenticated: false,
      loading: false,
      error: null,
    };
  } catch {
    return {
      user: null,
      isAuthenticated: false,
      loading: false,
      error: null,
    };
  }
};

const initialState = loadAuthState();

const authSlice = createSlice({
  name: "auth",
  initialState,
  reducers: {
    loginSuccess: (state, action) => {
      clearUserScopedCache();
      const normalizedSession = persistAuthSession(action.payload);

      state.isAuthenticated = hasAuthTokens(normalizedSession);
      state.user = normalizedSession;
      state.loading = false;
      state.error = null;
    },

    logoutSuccess: (state) => {
      state.isAuthenticated = false;
      state.user = null;
      state.loading = false;
      state.error = null;
      clearStoredAuthSession();
    },

    refreshTokenSuccess: (state, action) => {
      if (state.user) {
        const nextSession = normalizeAuthSession({
          ...state.user,
          accessToken: action.payload.accessToken,
          token: action.payload.accessToken,
          refreshToken:
            action.payload.refreshToken || state.user.refreshToken,
        });

        state.user = persistAuthSession(nextSession);
        state.isAuthenticated = hasAuthTokens(state.user);
      }
    },

    // Merge fields (e.g. service_id / service_name) into the logged-in user and persist, so every
    // consumer of the session (sidebar, guards) sees them immediately and after a refresh.
    updateSessionUser: (state, action) => {
      if (!state.user) return;
      const currentUser = state.user.user || state.user.userDetails || {};
      const mergedUser = { ...currentUser, ...action.payload };
      const nextSession = normalizeAuthSession({
        ...state.user,
        user: mergedUser,
        userDetails: mergedUser,
      });
      state.user = persistAuthSession(nextSession);
    },

    createUserRequest: (state) => {
      state.loading = true;
      state.error = null;
    },

    createUserSuccess: (state) => {
      clearUserScopedCache();
      state.loading = false;
      state.error = null;
    },

    createUserFailure: (state, action) => {
      state.loading = false;
      state.error = action.payload;
    },
  },
});

export const {
  loginSuccess,
  logoutSuccess,
  refreshTokenSuccess,
  updateSessionUser,
  createUserRequest,
  createUserSuccess,
  createUserFailure,
} = authSlice.actions;

export default authSlice.reducer;
